import { ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";
import { Controller, Control, FieldValues, Path } from "react-hook-form";
import { TextArea } from "@carbon/react";

type CarbonTextAreaProps = ComponentPropsWithoutRef<typeof TextArea>;

export type TextareaFieldProps<TFieldValues extends FieldValues> = Omit<
  CarbonTextAreaProps,
  | "id"
  | "labelText"
  | "invalid"
  | "invalidText"
  | "name"
  | "onChange"
  | "onBlur"
  | "value"
  | "defaultValue"
  | "ref"
> & {
  /** The RHF field name — also becomes the textarea's `id`, the same
   * convention every sibling in this layer uses. */
  name: Path<TFieldValues>;
  /** The field's visible label (already translated by the caller). */
  label: string;
  control: Control<TFieldValues>;
  /** Appends the translated `form.optional` marker ("(optional)") to `label`. Same convention
   * and rationale as `TextField`'s `optional` — see that component's docstring. */
  optional?: boolean;
};

/**
 * A Carbon `TextArea` bound to a React Hook Form field via `Controller` — the `TextField`
 * counterpart for multi-line text, anticipated since the Users flow (the programme spec's own
 * words) and now needed by Tags' description field.
 *
 * Same contract as every sibling in this layer: `invalid`/`invalidText` come straight from
 * `fieldState.error`, a real `<label htmlFor>` ties the label to the textarea's `id`
 * (`getByLabelText`/`getByRole("textbox", { name: label })` both resolve), and the field is
 * fully controlled from `field.value` every render — no local state that can drift.
 *
 * Carbon's error-association attribute was measured, not assumed, against the documented
 * `TextInput`/`Select` asymmetry (`aria-errormessage` vs. `aria-describedby`): reading
 * `@carbon/react`'s shipped `TextArea` source shows it emits `aria-errormessage` on the
 * `<textarea>` when `invalid` is set — the same attribute `TextInput` uses, not `Select`'s
 * `aria-describedby`. So this primitive needs no bespoke error-resolution logic beyond what
 * `TextField` already established.
 *
 * `enableCounter`/`maxCount` pass straight through to Carbon's own character/word counter (the
 * current Tags screen counts description length) — no reimplementation here, Carbon already
 * renders and announces it.
 */
function TextareaField<TFieldValues extends FieldValues>({
  name,
  label,
  control,
  optional,
  ...rest
}: TextareaFieldProps<TFieldValues>) {
  const { t } = useTranslation();
  const labelText = optional ? `${label} ${t("form.optional")}` : label;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextArea
          {...rest}
          {...field}
          id={name}
          labelText={labelText}
          invalid={Boolean(fieldState.error)}
          invalidText={fieldState.error?.message}
        />
      )}
    />
  );
}

export default TextareaField;
